import { createContext, useContext, type Provider } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { fg } from '@atlaskit/platform-feature-flags';

import { type ConversationAssistantConfig, type ProfileData } from '../../../types';
import { AI_MATE_EXPERIENCE_ID, FEEDBACK_COLLECTOR_ENTRYPOINT_ID } from '../../constants/private';

type ProductKey =
	| 'confluence'
	| 'jira'
	| 'atlas'
	| 'bitbucket'
	| 'admin'
	| 'store'
	| 'unknown'
	| '';

export const serviceProductToProductKey = (
	product: ConversationAssistantConfig['product'],
): ProductKey => {
	switch (product) {
		case 'confluence':
			return 'confluence';
		case 'jira-software':
		case 'jira-core':
		case 'jsm':
		case 'jpd':
		case 'jira':
			return 'jira';
		case 'atlassian-studio':
		case 'rovo':
		case 'atlas':
			return 'atlas';
		case 'bitbucket':
			return 'bitbucket';
		case 'admin':
			return 'admin';
		case 'store':
			return 'store';
		default:
			return 'unknown';
	}
};

export type GlobalConfig = {
	profileData: ProfileData;
	feedbackCollector: {
		entryPointId: string;
		displaySubmitFlag?: boolean;
	};
	/**
	 * This is the raw product header that is meant to be passed to assistanceService as `X-product` header
	 */
	productHeader: string;
	/**
	 * This is derived value from `productHeader`, there is a mapping to group product to known product for certain endpoint (e.g. `rovo` is an unknown product, but `atlas` is)
	 */
	productKey: ProductKey;
	closeChat: () => void;
	emojiProvider: Promise<EmojiProvider> | undefined;
} & Pick<
	ConversationAssistantConfig,
	| 'siteId'
	| 'workspaceId'
	| 'orgId'
	| 'preventAbortRequestsOnStagingModalClose'
	| 'preventAbortRequestsOnStandaloneChatClose'
	| 'renderingTouchpoint'
	| 'inProductExperience'
> &
	Required<Pick<ConversationAssistantConfig, 'experienceId'>> &
	Pick<ConversationAssistantConfig, 'assistanceServiceConfig'>;

const initialState: GlobalConfig = {
	profileData: { name: 'John Doe' },
	feedbackCollector: {
		entryPointId: '',
	},
	siteId: '',
	experienceId: '',
	productKey: '',
	productHeader: '',
	orgId: '',
	assistanceServiceConfig: undefined,
	closeChat: () => {},
	emojiProvider: undefined,
	preventAbortRequestsOnStagingModalClose: false,
	preventAbortRequestsOnStandaloneChatClose: false,
	inProductExperience: '',
};

const globalConfig = createContext<GlobalConfig>(initialState);
export const GlobalConfigProvider: Provider<GlobalConfig> = globalConfig.Provider;
export const useGlobalConfig = (): GlobalConfig => useContext(globalConfig);

export type CreateGlobalConfigArgs = ConversationAssistantConfig & {
	profileData: ProfileData;
	closeChat: () => void;
};

export const createGlobalConfig = ({
	profileData,
	displaySubmitFlag,
	siteId,
	product,
	experienceId = AI_MATE_EXPERIENCE_ID,
	workspaceId,
	orgId,
	assistanceServiceConfig,
	closeChat,
	emojiProvider,
	preventAbortRequestsOnStagingModalClose,
	preventAbortRequestsOnStandaloneChatClose,
	renderingTouchpoint,
	inProductExperience,
}: CreateGlobalConfigArgs): GlobalConfig => ({
	profileData,
	feedbackCollector: {
		entryPointId: FEEDBACK_COLLECTOR_ENTRYPOINT_ID,
		displaySubmitFlag,
	},
	siteId,
	experienceId,
	workspaceId,
	productKey: serviceProductToProductKey(product),
	productHeader: product,
	orgId,
	assistanceServiceConfig,
	closeChat,
	emojiProvider,
	preventAbortRequestsOnStagingModalClose,
	preventAbortRequestsOnStandaloneChatClose,
	...(fg('fe_enable_sa_externally') && { renderingTouchpoint }),
	inProductExperience,
});
